/**
 * Derives the Phase 1 events from the UI store's signals (screen, match setup, results, overlay, net, tutorial
 * progress), so no gameplay / sim / netcode code changes. Everything goes through `track()` (consent-gated).
 *
 * Definitions where the spec leaves room:
 *  - match modes: cpu → bot; online_quick / private → online_room; online_ranked → online_ranked. Local 2-player,
 *    practice and Football 101 lessons are not "matches" (no match_* events, no match_index).
 *  - tutorial_started: the first Football 101 lesson ever entered; tutorial_completed: all lessons done (step_reached
 *    = lesson count); tutorial_skipped: a lesson was skipped (step_reached = that lesson's number).
 *  - online_connect: success once the lobby accepted the request (room joined / created, or queued for ranked or quick).
 */
import { effect } from '@preact/signals-core';
import { DIFFICULTY_NAMES, type Difficulty } from '@hf/shared';
import { getPlatform } from '../platform';
import type { OnlineClient } from '../net/types';
import { onlineCharacter, pendingMatch } from '../ui-model/flow';
import type { UiStore } from '../ui-model/store';
import type { MatchResultsView, MatchSetup } from '../ui-model/types';
import { LESSONS } from '../ui-model/tutorial/catalog';
import { graduated, progress } from '../ui-model/tutorial/progress';
import type { AnalyticsMode, AnalyticsResult } from './events';
import {
  activeMatch,
  currentMatchIndex,
  isOptedIn,
  nextMatchIndex,
  onAnalyticsChange,
  setActiveMatch,
  track,
  trackResume,
} from './analytics';

export const modeOf = (setup: MatchSetup | null): AnalyticsMode | null => {
  switch (setup?.mode) {
    case 'cpu':
      return 'bot';
    case 'online_quick':
    case 'private':
      return 'online_room';
    case 'online_ranked':
      return 'online_ranked';
    default:
      return null;
  }
};

const humanIndex = (setup: MatchSetup): 0 | 1 => {
  if (setup.online) return setup.online.you;
  return setup.slots[0].kind === 'human' ? 0 : 1;
};

export const resultFor = (setup: MatchSetup, r: MatchResultsView): AnalyticsResult => {
  if (r.winner === -1) return 'draw';
  return r.winner === humanIndex(setup) ? 'win' : 'loss';
};

const secs = (ms: number): number => Math.max(0, Math.round(ms / 1000));

/** Fired by the app controller's `rematch` action. */
export const trackRematchTapped = (ui: UiStore): void => {
  const setup = ui.matchSetup.peek();
  const results = ui.results.peek();
  const mode = modeOf(setup);
  if (!setup || !results || !mode) return;
  track('rematch_tapped', { mode, previous_result: resultFor(setup, results) });
};

interface Session {
  setup: MatchSetup;
  mode: AnalyticsMode;
  startedAt: number;
  resultsAtStart: MatchResultsView | null;
  index: number;
  lost: boolean;
}

export const installAnalyticsTracking = (ui: UiStore): (() => void) => {
  const platform = getPlatform();
  const now = (): number => platform.now();
  const disposers: (() => void)[] = [];
  let session: Session | null = null;

  // ── launch: a match left open by a killed app ────────────────────────────────────────────────
  const reportKilledMatch = (): void => {
    const m = activeMatch();
    if (!m) return;
    setActiveMatch(null);
    const mode = m.mode as AnalyticsMode;
    track('match_abandoned', {
      mode,
      duration_s: secs(Math.max(0, m.lastSeenAt - m.startedAt)),
      reason: 'app_background',
    });
  };
  reportKilledMatch();
  disposers.push(
    onAnalyticsChange(() => {
      if (!isOptedIn()) setActiveMatch(null);
    }),
  );

  // ── visibility: keep the abandon marker fresh; foreground = app_opened (warm) ────────────────
  const vis = platform.visibility();
  if (vis) {
    let wasHidden = vis.hidden;
    disposers.push(
      vis.subscribe(() => {
        const hidden = vis.hidden;
        if (hidden && session) {
          const a = activeMatch();
          if (a) setActiveMatch({ ...a, lastSeenAt: Date.now() });
        }
        if (wasHidden && !hidden) trackResume();
        wasHidden = hidden;
      }),
    );
  }

  const endSession = (): void => {
    session = null;
    setActiveMatch(null);
  };

  // ── screen transitions: match start / completion / abandon, tutorial start ───────────────────
  let prevScreen = ui.screen.peek();
  disposers.push(
    effect(() => {
      const screen = ui.screen.value;
      const prev = prevScreen;
      prevScreen = screen;
      if (screen === prev) return;
      if (screen === 'match') {
        const setup = ui.matchSetup.peek();
        if (setup?.mode === 'tutorial') {
          if (progress.peek().last === null) track('tutorial_started', { step_reached: 1 });
          return;
        }
        const mode = modeOf(setup);
        if (!setup || !mode) return;
        const index = nextMatchIndex();
        const cpu = setup.slots.find((s) => s.kind === 'cpu');
        session = {
          setup,
          mode,
          startedAt: now(),
          resultsAtStart: ui.results.peek(),
          index,
          lost: false,
        };
        setActiveMatch({ mode, startedAt: Date.now(), lastSeenAt: Date.now() });
        track('match_started', {
          mode,
          bot_difficulty:
            cpu?.difficulty !== undefined ? DIFFICULTY_NAMES[cpu.difficulty as Difficulty] : 'none',
          head_id: setup.slots[humanIndex(setup)].character,
          match_index: index,
        });
      } else if (prev === 'match' && session) {
        const s = session;
        const results = ui.results.peek();
        const duration_s = secs(now() - s.startedAt);
        if (results && results !== s.resultsAtStart) {
          const [a, b] = results.lines;
          const me = humanIndex(s.setup);
          track('match_completed', {
            mode: s.mode,
            result: resultFor(s.setup, results),
            duration_s,
            score_for: me === 0 ? a.score : b.score,
            score_against: me === 0 ? b.score : a.score,
            match_index: s.index,
          });
        } else {
          track('match_abandoned', { mode: s.mode, duration_s, reason: s.lost ? 'disconnect' : 'quit' });
        }
        endSession();
      }
    }),
  );

  // ── connection lost in a match ───────────────────────────────────────────────────────────────
  disposers.push(
    effect(() => {
      const o = ui.overlay.value;
      if (!session || ui.screen.peek() !== 'match' || (o !== 'disconnected' && o !== 'desynced')) return;
      if (session.lost) return;
      session.lost = true;
      if (session.mode !== 'bot')
        track('connection_lost', {
          mode: session.mode,
          phase: 'in_match',
          match_elapsed_s: secs(now() - session.startedAt),
        });
    }),
  );

  // ── connection lost in the lobby ─────────────────────────────────────────────────────────────
  let prevNet = ui.net.peek().status;
  disposers.push(
    effect(() => {
      const n = ui.net.value;
      const prev = prevNet;
      prevNet = n.status;
      if (n.status === prev || ui.screen.peek() === 'match') return;
      const wasUp = prev === 'online' || prev === 'queueing' || prev === 'in_room';
      const dropped = n.status === 'error' || (n.status === 'connecting' && /dropped/i.test(n.message ?? ''));
      if (wasUp && dropped)
        track('connection_lost', {
          mode: n.queue?.mode === 'ranked' ? 'online_ranked' : 'online_room',
          phase: 'lobby',
          match_elapsed_s: 0,
        });
    }),
  );

  // ── head picked ──────────────────────────────────────────────────────────────────────────────
  let lastPending = pendingMatch.peek();
  disposers.push(
    effect(() => {
      const p = pendingMatch.value;
      if (p === lastPending) return;
      lastPending = p;
      if (!p || p.setup.online || p.setup.mode !== 'cpu') return;
      track('head_picked', {
        head_id: p.setup.slots[0].character,
        context: currentMatchIndex() === 0 ? 'first_run' : 'pre_match',
      });
    }),
  );
  let lastHead = onlineCharacter.peek();
  let headTimer: ReturnType<typeof setTimeout> | undefined;
  disposers.push(
    effect(() => {
      const h = onlineCharacter.value;
      if (h === lastHead) return;
      lastHead = h;
      if (headTimer) clearTimeout(headTimer);
      headTimer = setTimeout(() => track('head_picked', { head_id: h, context: 'menu' }), 1500);
    }),
    () => headTimer && clearTimeout(headTimer),
  );

  // ── Football 101 completion / skip ───────────────────────────────────────────────────────────
  let prevLessons = progress.peek().lessons;
  disposers.push(
    effect(() => {
      const p = progress.value;
      const before = prevLessons;
      prevLessons = p.lessons;
      for (const l of LESSONS) {
        const was = before[l.id]?.status;
        const rec = p.lessons[l.id];
        if (rec?.status === 'skipped' && was !== 'skipped' && was !== 'done')
          track('tutorial_skipped', { step_reached: l.number });
      }
      if (graduated(p) && !graduated({ ...p, lessons: before }))
        track('tutorial_completed', { step_reached: LESSONS.length });
    }),
  );

  return () => {
    for (const d of disposers.splice(0)) d();
  };
};

const CONNECT_TIMEOUT_MS = 20_000;

/** Wraps the lobby client to report `online_connect` for createRoom / joinRoom / queue. Pure decoration. */
export const instrumentOnline = (online: OnlineClient, ui: UiStore): OnlineClient => {
  let pending: {
    type: 'room_create' | 'room_join' | 'ranked';
    t0: number;
    timer: ReturnType<typeof setTimeout>;
  } | null = null;
  const clear = (): void => {
    if (pending) clearTimeout(pending.timer);
    pending = null;
  };
  const finish = (success: boolean, reason?: 'timeout' | 'lobby_error' | 'ice_failed' | 'other'): void => {
    if (!pending) return;
    const { type, t0 } = pending;
    clear();
    track('online_connect', {
      type,
      success,
      connect_ms: Math.max(0, Math.round(getPlatform().now() - t0)),
      ...(success ? {} : { failure_reason: reason ?? 'other' }),
    });
  };
  const begin = (type: 'room_create' | 'room_join' | 'ranked'): void => {
    clear();
    pending = {
      type,
      t0: getPlatform().now(),
      timer: setTimeout(() => finish(false, 'timeout'), CONNECT_TIMEOUT_MS),
    };
  };
  effect(() => {
    const n = ui.net.value;
    if (!pending) return;
    const ok =
      pending.type === 'ranked'
        ? n.status === 'queueing' || n.status === 'matched' || n.status === 'in_match'
        : n.status === 'in_room' || n.status === 'matched' || n.status === 'in_match';
    if (ok) return finish(true);
    if (n.status === 'error') {
      const m = n.message ?? '';
      finish(false, /ice|webrtc|peer/i.test(m) ? 'ice_failed' : /time/i.test(m) ? 'timeout' : 'lobby_error');
    }
  });
  const wrapped: Partial<OnlineClient> = {
    queue: (mode, character) => {
      begin('ranked');
      online.queue(mode, character);
    },
    createRoom: (character, settings) => {
      begin('room_create');
      online.createRoom(character, settings);
    },
    joinRoom: (code, character) => {
      begin('room_join');
      online.joinRoom(code, character);
    },
    leaveQueue: () => {
      clear();
      online.leaveQueue();
    },
    leaveRoom: () => {
      clear();
      online.leaveRoom();
    },
  };
  return new Proxy(online, {
    get(target, prop) {
      if (prop in wrapped) return wrapped[prop as keyof OnlineClient];
      const v = Reflect.get(target, prop, target) as unknown;
      return typeof v === 'function' ? (v as (...a: unknown[]) => unknown).bind(target) : v;
    },
  });
};
